import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, Group, Modal, SimpleGrid, UnstyledButton, ActionIcon, Avatar, Menu, Indicator } from '@mantine/core'
import {
  IconLogout, IconUser, IconBell, IconLayoutDashboard, IconPackage,
  IconScan, IconClipboardList, IconMenu2,
} from '@tabler/icons-react'
import { useAuth } from '../../hooks/useAuth'
import { NAV_GROUPS, isNavItemVisible, BrandMark } from './Layout'
import { getNotifications, getPendingBorrows, getHandovers } from '../../api/client'
import { PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'

/**
 * NativeLayout — เชลล์ของแอปตอนรันเป็น native app (Capacitor) โดยเฉพาะ
 * ไม่ใช้ sidebar แบบเว็บเลย ออกแบบใหม่ทั้งหมดให้เข้ากับมือถือ:
 * Header เรียบๆ ด้านบน (แจ้งเตือน + โปรไฟล์) + เนื้อหา + แถบไอคอนด้านล่างคงที่ 5 ปุ่ม
 * (แดชบอร์ดทรัพย์สิน / คลัง / สแกน-ตรงกลาง / รายงาน / เมนู)
 *
 * เลือกใช้ layout นี้แทน Layout.tsx (เว็บ/PWA) จาก App.tsx โดยเช็ค
 * Capacitor.isNativePlatform()
 */

// แถบล่างคงที่ 5 ปุ่มตามที่ตกลงกันไว้ — ไม่ใช้ priority-based แบบเดิมอีกต่อไป
// label ที่นี่คงที่เฉพาะมือถือ ไม่ใช้ labelKey ของ NAV_GROUPS (ซึ่งยาวเกินไปสำหรับ tab แคบๆ)
const BOTTOM_NAV_FIXED = [
  { path: '/dashboard', label: 'Home', icon: IconLayoutDashboard },              // ตามที่ขอ: ไม่ต้องแปล ให้เป็น "Home" เสมอ
  { path: '/inventory', labelKey: 'nav.stockShort', icon: IconPackage },
  { path: '/scan',      labelKey: 'nav.scan',       icon: IconScan, center: true },
  { path: '/reports',   labelKey: 'nav.reports',    icon: IconClipboardList },
]

export default function NativeLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    username, fullName, roles, isSuperAdmin, companyLogoUrl, companyName,
    doLogout, hasPermission, hasFeature, featuresLoaded, hasPageAccess,
  } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const isSuperAdminRole = isSuperAdmin || roles.includes('SUPER_ADMIN')
  const isAdminRole = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN')
  const ctx = { isSuperAdminRole, isAdminRole, roles, featuresLoaded, hasPermission, hasFeature, hasPageAccess }

  const visibleItems = NAV_GROUPS.flatMap(g => g.items).filter(i => isNavItemVisible(i, ctx))
  // เอาเฉพาะปุ่มที่ user มีสิทธิ์เข้าจริงจากชุดคงที่ 5 ปุ่ม (เผื่อบาง role ไม่เห็น Inventory เป็นต้น)
  const bottomNav = BOTTOM_NAV_FIXED.filter(fixedItem =>
    visibleItems.some(v => v.path === fixedItem.path)
  )

  const currentItem = visibleItems.find(i => i.path === location.pathname)

  // ── ขอสิทธิ์แจ้งเตือน (Android 13+ ต้องขอ runtime permission) ──────────────
  // หมายเหตุ: ตอนนี้ขอแค่ "สิทธิ์" ก่อนเท่านั้น (requestPermissions ไม่ต้องพึ่ง
  // Firebase เลย) — ตั้งใจไม่เรียก PushNotifications.register() ที่นี่ เพราะ
  // เมธอดนั้นพยายามขอ FCM token จาก Firebase โดยตรง ถ้ายังไม่มี
  // google-services.json อยู่ในโปรเจกต์ จะ "crash แอปทั้งแอปที่ระดับ native"
  // ทันที (ไม่ใช่แค่ throw error ธรรมดาที่ JS try/catch ดักได้ — เกิดขึ้นก่อน
  // Promise จะ reject กลับมาที่ฝั่ง JS ด้วยซ้ำ) เจอบั๊กนี้จริงจากการทดสอบแล้ว
  // (ปิด permission แจ้งเตือน = ข้าม register() ไปเลย = ไม่ crash ตรงกับพฤติกรรม
  // ที่เจอเป๊ะ) — จะเปิด register() คืนก็ต่อเมื่อมี Firebase project + วาง
  // google-services.json ไว้ใน backend/android/app/ แล้วเท่านั้น
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    PushNotifications.requestPermissions().catch(() => {})
  }, [])

  // แจ้งเตือน (unread) — เอามาแสดงเป็น badge ที่กระดิ่ง เหมือนเว็บ แค่ตัดหน้า popover ออก
  // เพราะบนมือถือกดแล้วควรพาไปหน้าเต็มจอ ไม่ใช่ dropdown เล็กๆ (จะทำหน้าเต็มจอในขั้นถัดไป)
  useEffect(() => {
    const load = async () => {
      try {
        const [notifs, pendingCanApprove] = await Promise.all([
          getNotifications().then(r => r.data ?? []).catch(() => []),
          hasPermission('APPROVE_BORROW')
            ? Promise.all([getPendingBorrows(), getHandovers()]).then(([b, h]) => {
                const pb = (b.data ?? []).filter((x: any) => x.status === 'PENDING').length
                const ph = (h.data ?? []).filter((x: any) => x.status === 'DRAFT').length
                return pb + ph
              }).catch(() => 0)
            : Promise.resolve(0),
        ])
        const unreadNotifs = notifs.filter((n: any) => !n.isRead).length
        setUnreadCount(unreadNotifs + pendingCanApprove)
      } catch { /* non-fatal */ }
    }
    load()
    const iv = setInterval(load, 60_000)
    return () => clearInterval(iv)
  }, [])

  return (
    <Box style={{ height: '100dvh', overflow: 'hidden', background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ── */}
      <Box style={{
        position: 'sticky', top: 0, zIndex: 100, background: '#0f172a',
        paddingTop: 'env(safe-area-inset-top)', flexShrink: 0,
      }}>
        <Group justify="space-between" px="md" py="sm" align="center">
          <Box style={{ minWidth: 0 }}>
            <BrandMark logoUrl={companyLogoUrl} name={companyName} />
            <Text size="xs" style={{ color: '#94a3b8', marginTop: 2 }}>
              {currentItem ? t(currentItem.labelKey) : ''}
            </Text>
          </Box>
          <Group gap={4}>
            <ActionIcon variant="subtle" radius="xl" size="lg" onClick={() => navigate('/notifications')}>
              <Indicator disabled={unreadCount === 0} color="red" size={8} offset={3}>
                <IconBell size={20} color="#cbd5e1" />
              </Indicator>
            </ActionIcon>
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" radius="xl" size="lg">
                  <Avatar size={26} radius="xl" color="teal">
                    {(fullName || username || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{fullName || username}</Menu.Label>
                <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navigate('/profile')}>
                  {t('nav.profile', 'Profile')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconLogout size={14} />}
                  onClick={() => { doLogout(); navigate('/login') }}>
                  {t('common.logout', 'Logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </Box>

      {/* ── Content — ส่วนเดียวที่เลื่อนได้จริง ส่วน header/bottom nav อยู่นอก scroll context
      นี้เลย ไม่มีทางขยับ/กระตุกตามการเลื่อนได้อีกต่อไป (ต่างจากเดิมที่ทั้งหน้าเลื่อนเป็นก้อนเดียว
      แล้วพึ่ง position: fixed ของ bottom nav ซึ่งบน Android WebView บางเครื่อง/บางจังหวะ
      คำนวณตำแหน่งใหม่ผิดจังหวะตอนเลื่อน ทำให้เห็นเมนูขยับเห็นได้ชัด) ── */}
      <Box style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Outlet />
      </Box>

      {/* ── Bottom Tab Bar — เป็น flex sibling ธรรมดา ไม่ใช้ position: fixed อีกต่อไป เพราะ
      shell ด้านนอกถูกล็อคความสูงไว้ที่ 100dvh + overflow: hidden แล้ว ไม่มีการเลื่อนของทั้งหน้า
      ให้ fixed ต้องคำนวณตำแหน่งใหม่เลย — 5 ปุ่มคงที่ ── */}
      <Box style={{
        flexShrink: 0,
        height: 68, background: '#0f172a',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'stretch', zIndex: 200,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {bottomNav.map((item) => {
          const active = location.pathname === item.path

          if (item.center) {
            // ปุ่ม Scan ตรงกลาง — วงกลมยกขึ้น เด่นกว่าปุ่มอื่น กดแล้วเข้ากล้องทันที
            return (
              <Box key={item.path} onClick={() => navigate(item.path)} style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer', position: 'relative',
              }}>
                <Box style={{
                  position: 'absolute', top: -22,
                  width: 52, height: 52, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(13,148,136,0.45)',
                  border: '4px solid #f1f5f9',
                }}>
                  <item.icon size={24} color="#fff" stroke={2} />
                </Box>
                <Text style={{ fontSize: 10, color: active ? '#6ee7b7' : '#475569', fontWeight: active ? 600 : 400, lineHeight: 1, marginTop: 34, whiteSpace: 'nowrap' }}>
                  {(item as any).label ?? t((item as any).labelKey)}
                </Text>
              </Box>
            )
          }

          return (
            <Box key={item.path} onClick={() => navigate(item.path)} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer',
              background: active ? 'rgba(110,231,183,0.08)' : 'transparent',
              borderTop: `2px solid ${active ? '#6ee7b7' : 'transparent'}`,
              transition: 'all 0.15s',
            }}>
              <item.icon size={20} color={active ? '#6ee7b7' : '#475569'} stroke={active ? 2 : 1.5} />
              <Text style={{ fontSize: 10, color: active ? '#6ee7b7' : '#475569', fontWeight: active ? 600 : 400, lineHeight: 1, whiteSpace: 'nowrap' }}>
                {(item as any).label ?? t((item as any).labelKey)}
              </Text>
            </Box>
          )
        })}
        <Box onClick={() => setMenuOpen(true)} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer',
        }}>
          <IconMenu2 size={20} color="#475569" stroke={1.5} />
          <Text style={{ fontSize: 10, color: '#475569', lineHeight: 1 }}>{t('nav.menu', 'Menu')}</Text>
        </Box>
      </Box>

      {/* ── Full menu grid ── */}
      <Modal opened={menuOpen} onClose={() => setMenuOpen(false)} fullScreen withCloseButton
        title={<Text fw={600}>{t('nav.menu', 'Menu')}</Text>}>
        <SimpleGrid cols={4} spacing="lg">
          {visibleItems.map((item) => (
            <UnstyledButton key={item.path}
              onClick={() => { navigate(item.path); setMenuOpen(false) }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Box style={{
                width: 52, height: 52, borderRadius: 16, background: '#f0fdfa',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <item.icon size={24} color="#0d9488" />
              </Box>
              <Text size="xs" ta="center" style={{ lineHeight: 1.2 }}>{t(item.labelKey)}</Text>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Modal>
    </Box>
  )
}

